/**
 * useAssetLookups
 * Loads the Asset Management lookup tables (status / category / type / condition)
 * once and exposes them as lists + id→entity maps for badges and filters.
 */

import { useQuery } from '@tanstack/react-query';
import {
  useAssetStatusService,
  useAssetCategoryService,
  useAssetTypeService,
  useAssetConditionService,
  type AssetStatus,
  type AssetCategory,
  type AssetType,
  type AssetCondition,
} from '@actschurch/ccms-sdk';

// Lookups change rarely, but a long staleTime is dangerous here: if the very
// first fetch returns [] (e.g. before the tables were seeded, or while the
// token was briefly expired -> 401), a long window would keep serving that
// empty result without ever refetching. Revalidate on mount (stale-while-
// revalidate) so seeded/changed lookups always appear on the next render.
const STALE = 0;

export function useAssetLookups() {
  const statusService = useAssetStatusService();
  const categoryService = useAssetCategoryService();
  const typeService = useAssetTypeService();
  const conditionService = useAssetConditionService();

  const { data: statuses = [] } = useQuery({
    queryKey: ['asset', 'statuses'],
    queryFn: () => statusService.getAll(),
    staleTime: STALE,
    refetchOnMount: true,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['asset', 'categories'],
    queryFn: () => categoryService.getAll(),
    staleTime: STALE,
    refetchOnMount: true,
  });
  const { data: types = [] } = useQuery({
    queryKey: ['asset', 'types'],
    queryFn: () => typeService.getAll(),
    staleTime: STALE,
    refetchOnMount: true,
  });
  const { data: conditions = [] } = useQuery({
    queryKey: ['asset', 'conditions'],
    queryFn: () => conditionService.getAll(),
    staleTime: STALE,
    refetchOnMount: true,
  });

  const getStatusById = (id?: number | null): AssetStatus | undefined =>
    id == null ? undefined : statuses.find((s) => s.statusID === id);
  const getCategoryById = (id?: number | null): AssetCategory | undefined =>
    id == null ? undefined : categories.find((c) => c.categoryID === id);
  const getTypeById = (id?: number | null): AssetType | undefined =>
    id == null ? undefined : types.find((t) => t.typeID === id);
  const getConditionById = (id?: number | null): AssetCondition | undefined =>
    id == null ? undefined : conditions.find((c) => c.conditionID === id);

  return {
    statuses,
    categories,
    types,
    conditions,
    getStatusById,
    getCategoryById,
    getTypeById,
    getConditionById,
  };
}
